import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchExperiences } from '../services/api';
import { localizedExperience } from '../utils/localizedContent';

function Experience({ isDarkMode }) {
  const { t, i18n } = useTranslation();
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('Work');

  useEffect(() => {
    const getExperiences = async () => {
      try {
        const data = await fetchExperiences();
        setExperiences(data);
      } catch (error) {
        console.error('Error fetching experiences: ', error);
      } finally {
        setLoading(false);
      }
    };
    getExperiences();
  }, []);

  const extractYear = (dateString) => {
    const yearMatch = dateString.match(/\b(20\d{2})\b/);
    return yearMatch ? parseInt(yearMatch[1], 10) : 0;
  };

  const sortExperiencesChronologically = useCallback((expList) => {
    return [...expList].sort((a, b) => {
      const yearA = extractYear(a.date || '');
      const yearB = extractYear(b.date || '');
      return yearA - yearB;
    });
  }, []);

  const filteredExperiences = useMemo(() => {
    const filtered = experiences.filter((exp) => exp.type === filter);
    const localized = filtered.map((exp) => localizedExperience(exp, i18n.language));
    return sortExperiencesChronologically(localized);
  }, [experiences, filter, i18n.language, sortExperiencesChronologically]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center transition-colors duration-300">
        <div className="text-center">
          <div className={`w-16 h-16 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4 transition-colors duration-300 ${
            isDarkMode ? 'border-purple-500' : 'border-blue-500'
          }`}></div>
          <p className={`text-lg transition-colors duration-300 ${
            isDarkMode ? 'text-gray-300' : 'text-gray-600'
          }`}>
            {t('experience.loading')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <section className="min-h-screen pt-4 pb-4 lg:py-16 px-4 sm:px-6 lg:px-8 transition-colors duration-300" id="experience">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-6 lg:mb-16">
          <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 lg:mb-6 transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            {t('experience.title')}
          </h1>
          <p className={`text-sm sm:text-base lg:text-xl max-w-2xl mx-auto transition-colors duration-300 mb-4 lg:mb-0 ${
            isDarkMode ? 'text-gray-300' : 'text-gray-600'
          }`}>
            {t('experience.subtitle')}
          </p>
        </div>

        <div className="flex justify-center mb-8 lg:mb-16">
          <div className={`inline-flex rounded-2xl p-2 transition-all duration-300 ${
            isDarkMode
              ? 'bg-gray-800/60 border border-gray-700/50 shadow-xl shadow-black/20'
              : 'bg-gray-100 border border-gray-200 shadow-xl shadow-gray-200/50'
          } backdrop-blur-sm`}>
            <button
              className={`px-8 py-3 rounded-xl text-sm font-semibold transition-all duration-300 ${
                filter === 'Work'
                  ? isDarkMode
                    ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-lg shadow-purple-500/25'
                    : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/25'
                  : isDarkMode
                    ? 'text-gray-300 hover:text-white hover:bg-gray-700/50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
              }`}
              onClick={() => setFilter('Work')}
              aria-label={t('experience.filterWorkAria')}
            >
              {t('experience.filters.work')}
            </button>
            <button
              className={`px-8 py-3 rounded-xl text-sm font-semibold transition-all duration-300 ${
                filter === 'Studies'
                  ? isDarkMode
                    ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-lg shadow-purple-500/25'
                    : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/25'
                  : isDarkMode
                    ? 'text-gray-300 hover:text-white hover:bg-gray-700/50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
              }`}
              onClick={() => setFilter('Studies')}
              aria-label={t('experience.filterStudiesAria')}
            >
              {t('experience.filters.studies')}
            </button>
          </div>
        </div>

        <div className="lg:hidden space-y-0">
          {filteredExperiences.map((experience) => (
            <div key={experience.id} className={`border-b last:border-b-0 ${
              isDarkMode ? 'border-gray-700/30' : 'border-gray-300/50'
            }`}>
              <div className="py-4 flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="flex-shrink-0 w-8 h-8 mt-0.5">
                    <img
                      src={!isDarkMode && experience.darkImageUrl ? experience.darkImageUrl : experience.imageUrl}
                      alt={experience.name}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`font-bold text-base mb-1 transition-colors duration-300 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {experience.name}
                    </h3>
                    <p className={`text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {experience.date}
                    </p>
                  </div>
                </div>

                <div className="flex-shrink-0 text-right">
                  <span className={`text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {experience.role}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="hidden lg:block relative">
          <div className={`absolute top-1/2 left-0 right-0 h-1 transform -translate-y-1/2 transition-all duration-300 ${
            isDarkMode
              ? 'bg-gradient-to-r from-purple-500/20 via-purple-400/10 to-purple-500/20'
              : 'bg-gradient-to-r from-blue-500/20 via-blue-400/10 to-blue-500/20'
          }`}></div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 relative z-10 mt-8">
            {filteredExperiences.map((experience, index) => (
              <div
                key={experience.id}
                className="group relative flex flex-col items-center"
              >
                <div className={`relative w-20 h-20 rounded-full flex items-center justify-center mb-6 transition-all duration-500 ${
                  isDarkMode
                    ? 'bg-gradient-to-br from-gray-800 to-gray-900 border-4 border-purple-500/30 shadow-2xl shadow-purple-500/20'
                    : 'bg-gradient-to-br from-white to-gray-50 border-4 border-blue-500/30 shadow-2xl shadow-blue-500/20'
                } group-hover:border-purple-500/60 group-hover:shadow-2xl group-hover:shadow-purple-500/30 transform group-hover:scale-110`}>
                  <div className="absolute inset-0 rounded-full transition-all duration-500 blur-xl group-hover:blur-2xl"></div>

                  <img
                    src={!isDarkMode && experience.darkImageUrl ? experience.darkImageUrl : experience.imageUrl}
                    alt={experience.name}
                    className="relative z-10 w-12 h-12 object-contain filter drop-shadow-lg transition-all duration-300"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>

                <div className={`w-full rounded-2xl p-6 text-center transform transition-all duration-500 ${
                  isDarkMode
                    ? 'bg-gradient-to-br from-gray-800/90 to-gray-900/95 border border-purple-400/20 shadow-2xl shadow-purple-500/10'
                    : 'bg-gradient-to-br from-white to-gray-50 border border-gray-200 shadow-2xl shadow-blue-500/10'
                } group-hover:border-purple-500/40 group-hover:shadow-2xl group-hover:shadow-purple-500/20 group-hover:-translate-y-2 min-h-[220px] flex flex-col justify-between backdrop-blur-sm`}>
                  <div className="mb-4">
                    <h3
                      className={`font-bold text-xl mb-2 line-clamp-2 transition-colors duration-300 ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}
                      title={experience.name}
                    >
                      {experience.name}
                    </h3>
                  </div>

                  <div className="mb-4">
                    <span className={`inline-block px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                      isDarkMode
                        ? 'bg-purple-500/20 text-purple-300 border border-purple-400/30'
                        : 'bg-blue-500/20 text-blue-700 border border-blue-400/30'
                    } backdrop-blur-sm`}>
                      {experience.role}
                    </span>
                  </div>

                  <p className={`text-sm leading-relaxed mb-6 flex-1 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {experience.description}
                  </p>

                  <div className="flex justify-center">
                    <span className={`inline-block px-4 py-2 rounded-full text-xs font-bold transition-all duration-300 ${
                      isDarkMode
                        ? 'bg-gray-800/80 text-white border border-gray-600/50 shadow-lg shadow-black/20'
                        : 'bg-gray-100 text-gray-700 border border-gray-300 shadow-lg shadow-gray-200/50'
                    } backdrop-blur-sm`}>
                      {experience.date}
                    </span>
                  </div>
                </div>

                {index < filteredExperiences.length - 1 && (
                  <div className={`hidden lg:block absolute top-10 -right-4 w-8 h-0.5 transition-all duration-300 ${
                    isDarkMode
                      ? 'bg-gradient-to-r from-purple-500/60 to-purple-500/20'
                      : 'bg-gradient-to-r from-blue-500/60 to-blue-500/20'
                  }`}></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default Experience;
